import i18n from "../../i18n";
import request from "../../utils/request";
import { Result } from "../../models/general";
import { requestUpgrade } from "../General/Subscription";

/**
 * Typed client for the self-serve billing portal (CO-05).
 *
 * TS mirrors of the server view models in
 * `Mowazaf.Server/Services/Billing/Abstractions/BillingServiceContracts.cs` and
 * `Mowazaf.Server/Services/Billing/Seats/SeatContracts.cs`.
 *
 * TWO INVARIANTS THIS FILE ENFORCES BY SHAPE, not by convention:
 *
 *  1. NO REQUEST CARRIES A `companyId`. The tenant comes from the authenticated
 *     principal on every `/api/Billing/*` route. There is deliberately no input
 *     model here with a company field, so there is no request shape a caller
 *     could build that reaches another tenant's billing data.
 *  2. NO REQUEST CARRIES AN AMOUNT. Every price, proration factor, tax figure
 *     and total is server-computed and arrives on a `BillingQuoteView` /
 *     `CancellationQuoteView`. The browser NEVER does billing arithmetic — it
 *     only formats numbers the server already decided.
 *
 * Enum fields are typed `Name | number` and read through the `coerce*` helpers,
 * matching the existing `coerceTier` idiom in `api/General/Subscription.ts`:
 * the API may serialize an enum as its name or its numeric value depending on
 * the converter in play, and a portal that renders "2" where it should render
 * "Active" is a support ticket.
 */

/* ───────────────────────── Enums (mirror BillingEnums.cs) ───────────────────────── */

export type BillingSubscriptionState =
  | "Draft"
  | "Trialing"
  | "Active"
  | "NonRenewing"
  | "PastDue"
  | "Cancelled"
  | "Bespoke";

export type BillingAccessState = "Full" | "Grace" | "Restricted" | "Dormant";

export type BillingInvoiceStatus =
  | "Draft"
  | "Issued"
  | "Paid"
  | "PartiallyRefunded"
  | "Refunded"
  | "Void"
  | "Uncollectible";

export type BillingInvoiceKind =
  | "Subscription"
  | "Renewal"
  | "SeatTrueUp"
  | "PlanChange"
  | "Manual";

export type BillingPaymentMethodStatus =
  | "Active"
  | "Expired"
  | "Removed"
  | "VerificationFailed";

export type BillingCheckoutStatus =
  | "Created"
  | "Redirected"
  | "ReturnedSuccess"
  | "ReturnedFailure"
  | "Completed"
  | "Expired"
  | "Abandoned";

export type BillingSeatChangeStatus = "Pending" | "Settled" | "Superseded" | "Rejected";

/**
 * WHO MAY CHANGE THIS SUBSCRIPTION. Mirrors `BillingManagementMode` in
 * `Data/General/Entities/Billing/BillingEnums.cs`.
 *
 * `SelfServe` — the customer started it, so the customer owns it.
 * `OperatorManaged` — Mowazaf established it on their behalf, so Mowazaf owns it and the
 * customer's portal is read-only with a route to ask for a change.
 */
export type BillingManagementMode = "SelfServe" | "OperatorManaged";

/**
 * HOW IT BEGAN. Immutable historical fact; mirrors `BillingSubscriptionOrigin`. Deliberately
 * separate from the mode above, because management can be handed back and forth while origin
 * cannot. `Unknown` is NEVER a reason to restrict anything — only the mode restricts.
 */
export type BillingSubscriptionOrigin =
  | "Unknown"
  | "SelfServeCheckout"
  | "OperatorProvisioned"
  | "Trial"
  | "Migrated";

/** Mirrors `BillingCustomerAction`. Which mutation the server refused. */
export type BillingCustomerAction =
  | "StartCheckout"
  | "ChangePlan"
  | "ChangeSeats"
  | "Cancel"
  | "SetAutoRenew"
  | "SetAutoTrueUp"
  | "AddPaymentMethod"
  | "SetDefaultPaymentMethod";

/** Mirrors `BillingCheckoutIntent`. Sent as the string name; the server binds either form. */
export type BillingCheckoutIntent =
  | "NewSubscription"
  | "UpdatePaymentMethod"
  | "PayInvoice"
  | "SeatTrueUp"
  | "PlanChange";

/* ───────────────────────── Catalogue (public) ───────────────────────── */

export interface BillingPlanPriceView {
  currency: string;
  termMonths: number;
  pricePerSeatPerMonth: number;
  listMonthlyReference: number;
  discountPercentFromMonthly: number;
  /** TermMonths x PricePerSeatPerMonth. PRICE(L). */
  fullTermPricePerSeat: number;
}

export interface BillingPlanView {
  code: string;
  name: string;
  description?: string | null;
  isSelfServe: boolean;
  minSeats: number;
  maxSeats?: number | null;
  displayOrder: number;
  featureKeys: string[];
  prices: BillingPlanPriceView[];
}

export interface PublicPricingView {
  currency: string;
  plans: BillingPlanView[];
  vatDisclosureKey: string;
  minSeatsPro: number;
  monthlyReferenceRate: number;
}

/* ───────────────────────── Overview ───────────────────────── */

export interface BillingPaymentMethodView {
  id: number;
  brand?: string | null;
  last4?: string | null;
  expiryMonth?: number | null;
  expiryYear?: number | null;
  status: BillingPaymentMethodStatus | number;
  isDefault: boolean;
}

export interface BillingAccessBannerView {
  accessState: BillingAccessState | number;
  graceEndedAtUtc?: string | null;
  restrictedAtUtc?: string | null;
  amountDueGross?: number | null;
  currency?: string | null;
  messageKey: string;
}

/**
 * Shipped on EVERY overview read, denied or not. Mirrors `BillingManagementNoticeView`.
 *
 * This is the field that lets the portal render read-only BEFORE the user clicks anything. The
 * previous story was `state === "Bespoke"`, which reproduced the server-side defect exactly: a
 * subscription an operator marked paid or extended by hand is `OperatorManaged` while its state is
 * NOT `Bespoke`, and the customer was offered the full self-serve UI over it.
 */
export interface BillingManagementNoticeView {
  customerManaged: boolean;
  managementMode: BillingManagementMode | number;
  origin: BillingSubscriptionOrigin | number;
  messageKey?: string | null;
  contactEmail?: string | null;
  requestChangeEndpoint?: string | null;
  operatorNote?: string | null;
}

/**
 * The machine-readable body of a 403 management denial, carried on `Error.details`. Mirrors
 * `BillingManagementDenialView`.
 *
 * ⚠️ THE UI IS NEVER THE ONLY GUARD. Management can change between the page load that decided to
 * show a button and the click that presses it, so every mutating call reads this off the failure
 * and renders the same read-only explanation the banner would have shown.
 */
export interface BillingManagementDenialView {
  code: string;
  action: BillingCustomerAction | number;
  managementMode: BillingManagementMode | number;
  origin: BillingSubscriptionOrigin | number;
  state: BillingSubscriptionState | number;
  messageKey: string;
  contactEmail?: string | null;
  requestChangeEndpoint?: string | null;
  operatorNote?: string | null;
}

export interface BillingOverviewView {
  isBillingManaged: boolean;
  planCode?: string | null;
  planName?: string | null;
  state?: BillingSubscriptionState | number | null;
  accessState?: BillingAccessState | number | null;
  currency?: string | null;
  termMonths?: number | null;
  committedSeats?: number | null;
  billableSeats?: number | null;
  effectiveRatePerSeatPerMonth?: number | null;
  termStartUtc?: string | null;
  termEndUtc?: string | null;
  nextChargeAtUtc?: string | null;
  nextChargeGross?: number | null;
  autoRenew: boolean;
  autoTrueUpSeats: boolean;
  rateFrozenUntilUtc?: string | null;
  creditBalance: number;
  defaultPaymentMethod?: BillingPaymentMethodView | null;
  accessBanner?: BillingAccessBannerView | null;
  /** Never null from a current server. Optional here only so an older build does not fail to parse. */
  management?: BillingManagementNoticeView | null;
  /**
   * The subscription never lapses on its own. DISCLOSED to the customer on purpose: an override the
   * customer cannot see is an override nobody can reconcile.
   *
   * ⚠️ Read-only here. There is no customer-facing way to set or clear it, and the customer is never
   * shown who granted it or why — that is operator-internal and would leak platform context.
   */
  neverExpires?: boolean | null;
}

/* ───────────────────────── Seats ───────────────────────── */

export interface SeatUsageView {
  committedSeats: number;
  billableSeats: number;
  minSeats: number;
  maxSeats?: number | null;
  overageSeats: number;
  overageSinceUtc?: string | null;
  trueUpDateUtc?: string | null;
  estimatedTrueUpNet?: number | null;
  currency: string;
  autoTrueUpSeats: boolean;
  /** Terminated/Resigned with no exit date. The fixable data-quality nudge. */
  unresolvedExitDateCount: number;
  /**
   * CO-52. An accepted-but-not-yet-settled seat change, or null when there is none.
   *
   * ⚠️ A seat ADDITION does not move `committedSeats` at request time — the row is written `Pending`
   * and the nightly settlement job commits the seats and raises the invoice. So `committedSeats`
   * legitimately still reads the OLD number right after a successful add, and this field is the only
   * thing that lets the portal say so. Rendering the panel without it reads as a failed action that
   * claimed to succeed. Removals settle inline and never populate this.
   */
  pendingChange?: PendingSeatChangeView | null;
}

export interface PendingSeatChangeView {
  seatChangeId: number;
  /** Signed NET delta after same-day merging — what will actually be charged/credited. */
  delta: number;
  effectiveDateUtc: string;
  /** Ex-VAT, absolute; direction is `delta`'s sign. Server-computed. */
  amountNet?: number | null;
  currency: string;
  /** committedSeats + delta — the commitment once the job settles it. */
  seatsAfterSettlement: number;
  requestedAtUtc?: string | null;
}

export interface SeatExplainRow {
  userId: string;
  displayName: string;
  billable: boolean;
  exclusionReasonKey?: string | null;
  hireDate?: string | null;
  exitDate?: string | null;
}

export interface SeatExplainView {
  asOfUtc: string;
  ruleVersion: number;
  totalUsers: number;
  excludedArchived: number;
  excludedNotStarted: number;
  excludedExited: number;
  excludedPlatform: number;
  unresolvedExitDateCount: number;
  billableSeats: number;
  committedSeats: number;
  rows: SeatExplainRow[];
}

/* ───────────────────────── Quotes ───────────────────────── */

export interface BillingQuoteLineView {
  descriptionKey: string;
  descriptionParamsJson?: string | null;
  quantity: number;
  unitPriceNet: number;
  prorationFactor: number;
  lineNet: number;
}

export interface BillingQuoteView {
  currency: string;
  seats: number;
  termMonths: number;
  ratePerSeatPerMonth: number;
  seatTermPriceNet: number;
  prorationFactor: number;
  subtotalNet: number;
  creditApplied: number;
  taxRatePercent: number;
  taxAmount: number;
  totalGross: number;
  periodStartUtc?: string | null;
  periodEndUtc?: string | null;
  requiresFreshCheckout: boolean;
  exceedsSelfServeCap: boolean;
  lines: BillingQuoteLineView[];
  /** SEAT_FLOOR_MIN_SEATS | SEAT_FLOOR_BILLABLE | SUBSCRIPTION_RESTRICTED | ... */
  blockReasonCode?: string | null;
  blockMessageKey?: string | null;
  /**
   * DIRECTION. Every money field above is an unsigned MAGNITUDE, so `totalGross >= 0` is always
   * true and can never tell "you owe us 2,646" from "we owe you 2,646". The server sets this from
   * ProrationDirection — never from a rate comparison, because on the multi-year ladder the per-seat
   * rate falls (22 → 19) while the term price rises (264 → 684).
   */
  isCredit?: boolean;
}

/* ───────────────────────── Inputs ───────────────────────── */

export interface NewSubscriptionQuoteInput {
  planCode: string;
  currency: string;
  termMonths: number;
  seats: number;
}

export interface StartCheckoutInput {
  intent: BillingCheckoutIntent;
  planCode?: string | null;
  currency: string;
  termMonths?: number | null;
  seats?: number | null;
  invoiceId?: number | null;
  returnUrl: string;
  locale: string;
}

export interface SeatChangeInput {
  /** Signed. Positive adds seats, negative removes them. */
  seatDelta: number;
  /** ISO yyyy-MM-dd. Omit for "today". */
  effectiveDateUtc?: string | null;
}

export interface PlanChangeInput {
  targetPlanCode: string;
  targetTermMonths?: number | null;
  /** Downgrades default to term end. Immediate downgrade yields account credit, not cash. */
  applyImmediately: boolean;
}

export interface CancellationInput {
  immediate: boolean;
  reasonCode?: string | null;
  notes?: string | null;
}

/* ───────────────────────── Results ───────────────────────── */

export interface BillingCheckoutStartedView {
  sessionToken: string;
  redirectUrl: string;
  expiresAtUtc: string;
  quote: BillingQuoteView;
}

export interface BillingCheckoutStatusView {
  sessionToken: string;
  status: BillingCheckoutStatus | number;
  subscriptionActivated: boolean;
  invoiceNumber?: string | null;
  failureReasonKey?: string | null;
}

export interface SeatChangeResultView {
  seatChangeId: number;
  seatsBefore: number;
  seatsAfter: number;
  netDelta: number;
  status: BillingSeatChangeStatus | number;
  effectiveDateUtc: string;
  amountNet?: number | null;
  creditLedgerId?: number | null;
  invoiceId?: number | null;
}

export interface PlanChangeResultView {
  planCode: string;
  termMonths: number;
  effectiveRatePerSeatPerMonth: number;
  effectiveAtUtc: string;
  chargedNet?: number | null;
  creditedNet?: number | null;
  invoiceId?: number | null;
  creditLedgerId?: number | null;
}

export interface CancellationQuoteView {
  consumedMonths: number;
  termMonths: number;
  seats: number;
  monthlyReferenceRate: number;
  consumedValueNet: number;
  paidNet: number;
  refundNet: number;
  taxRefunded: number;
  refundGross: number;
  currency: string;
  reRateFactor: number;
  rulePath: string;
  requiresHumanApproval: boolean;
  effectiveAtUtc: string;
  dataPromiseKey: string;
}

export interface CancellationResultView {
  state: BillingSubscriptionState | number;
  accessState: BillingAccessState | number;
  effectiveAtUtc: string;
  refundId?: number | null;
  refundGross?: number | null;
  creditNoteNumber?: string | null;
  currency?: string | null;
}

/* ───────────────────────── Invoices + profile ───────────────────────── */

export interface BillingInvoiceRowView {
  id: number;
  invoiceNumber?: string | null;
  kind: BillingInvoiceKind | number;
  status: BillingInvoiceStatus | number;
  currency: string;
  subtotalNet: number;
  creditApplied: number;
  taxRatePercent: number;
  taxAmount: number;
  totalGross: number;
  amountDue: number;
  amountPaid: number;
  amountRefunded: number;
  periodStartUtc?: string | null;
  periodEndUtc?: string | null;
  issuedAtUtc?: string | null;
  dueAtUtc?: string | null;
  paidAtUtc?: string | null;
}

export interface BillingCreditView {
  currency: string;
  balance: number;
  cashOriginBalance: number;
}

export interface BillingProfileView {
  legalName: string;
  billingEmail: string;
  billingContactName?: string | null;
  addressLine1: string;
  addressLine2?: string | null;
  city?: string | null;
  countryId?: number | null;
  /** ISO-2. "AE" | "SA" | other. */
  taxCountry: string;
  taxRegistrationNumber?: string | null;
  taxRegistrationVerifiedAtUtc?: string | null;
  preferredCurrency: string;
  preferredLocale: string;
  poNumber?: string | null;
}

/* ────────────── Enum coercion (numbers OR strings) ────────────── */

const SUBSCRIPTION_STATE_NAMES: BillingSubscriptionState[] = [
  "Draft",
  "Trialing",
  "Active",
  "NonRenewing",
  "PastDue",
  "Cancelled",
  "Bespoke",
];

const ACCESS_STATE_NAMES: BillingAccessState[] = ["Full", "Grace", "Restricted", "Dormant"];

const INVOICE_STATUS_NAMES: BillingInvoiceStatus[] = [
  "Draft",
  "Issued",
  "Paid",
  "PartiallyRefunded",
  "Refunded",
  "Void",
  "Uncollectible",
];

const INVOICE_KIND_NAMES: BillingInvoiceKind[] = [
  "Subscription",
  "Renewal",
  "SeatTrueUp",
  "PlanChange",
  "Manual",
];

const PAYMENT_METHOD_STATUS_NAMES: BillingPaymentMethodStatus[] = [
  "Active",
  "Expired",
  "Removed",
  "VerificationFailed",
];

const CHECKOUT_STATUS_NAMES: BillingCheckoutStatus[] = [
  "Created",
  "Redirected",
  "ReturnedSuccess",
  "ReturnedFailure",
  "Completed",
  "Expired",
  "Abandoned",
];

function coerceEnum<T extends string>(
  value: T | number | null | undefined,
  names: readonly T[],
  fallback: T,
): T {
  if (typeof value === "number") return names[value] ?? fallback;
  if (typeof value === "string" && (names as readonly string[]).includes(value)) return value as T;
  return fallback;
}

/**
 * `Draft` is the fallback rather than `Active`: an unknown value must never be
 * rendered as a healthy paid subscription. Failing towards "nothing is set up
 * yet" is recoverable; failing towards "you're fine" hides a lapse.
 */
export function coerceState(
  value: BillingSubscriptionState | number | null | undefined,
): BillingSubscriptionState {
  return coerceEnum(value, SUBSCRIPTION_STATE_NAMES, "Draft");
}

/**
 * `Full` is the fallback deliberately. An unreadable access state must not
 * degrade a paying customer's app into a restricted banner: the SERVER blocks
 * writes, so failing open on the client is cosmetic, while failing closed would
 * show a false lockout notice to someone who paid.
 */
export function coerceAccessState(
  value: BillingAccessState | number | null | undefined,
): BillingAccessState {
  return coerceEnum(value, ACCESS_STATE_NAMES, "Full");
}

export function coerceInvoiceStatus(
  value: BillingInvoiceStatus | number | null | undefined,
): BillingInvoiceStatus {
  return coerceEnum(value, INVOICE_STATUS_NAMES, "Draft");
}

export function coerceInvoiceKind(
  value: BillingInvoiceKind | number | null | undefined,
): BillingInvoiceKind {
  return coerceEnum(value, INVOICE_KIND_NAMES, "Subscription");
}

export function coercePaymentMethodStatus(
  value: BillingPaymentMethodStatus | number | null | undefined,
): BillingPaymentMethodStatus {
  return coerceEnum(value, PAYMENT_METHOD_STATUS_NAMES, "Active");
}

const MANAGEMENT_MODE_NAMES: BillingManagementMode[] = ["SelfServe", "OperatorManaged"];

const ORIGIN_NAMES: BillingSubscriptionOrigin[] = [
  "Unknown",
  "SelfServeCheckout",
  "OperatorProvisioned",
  "Trial",
  "Migrated",
];

const SEAT_CHANGE_STATUS_NAMES: BillingSeatChangeStatus[] = [
  "Pending",
  "Settled",
  "Superseded",
  "Rejected",
];

const CUSTOMER_ACTION_NAMES: BillingCustomerAction[] = [
  "StartCheckout",
  "ChangePlan",
  "ChangeSeats",
  "Cancel",
  "SetAutoRenew",
  "SetAutoTrueUp",
  "AddPaymentMethod",
  "SetDefaultPaymentMethod",
];

export function coerceManagementMode(
  value: BillingManagementMode | number | null | undefined,
): BillingManagementMode {
  return coerceEnum(value, MANAGEMENT_MODE_NAMES, "SelfServe");
}

export function coerceSubscriptionOrigin(
  value: BillingSubscriptionOrigin | number | null | undefined,
): BillingSubscriptionOrigin {
  return coerceEnum(value, ORIGIN_NAMES, "Unknown");
}

export function coerceCustomerAction(
  value: BillingCustomerAction | number | null | undefined,
): BillingCustomerAction {
  return coerceEnum(value, CUSTOMER_ACTION_NAMES, "StartCheckout");
}

/**
 * `Pending` is the fallback on purpose: an unrecognised status must never be rendered as `Settled`.
 * Telling a customer their seats are live when the settlement job has not run yet is the exact
 * defect CO-52 is about; "still pending" is the recoverable direction to be wrong in.
 */
export function coerceSeatChangeStatus(
  value: BillingSeatChangeStatus | number | null | undefined,
): BillingSeatChangeStatus {
  return coerceEnum(value, SEAT_CHANGE_STATUS_NAMES, "Pending");
}

export function coerceCheckoutStatus(
  value: BillingCheckoutStatus | number | null | undefined,
): BillingCheckoutStatus {
  return coerceEnum(value, CHECKOUT_STATUS_NAMES, "Created");
}

/* ───────────────────────── Error surfacing ───────────────────────── */

/**
 * Pulls the human-readable text off a failed `Result<T>`.
 *
 * The `Error` model in `models/general.ts` carries `description`, NOT `message`.
 * A large amount of existing code reads `errors[0].message`, gets `undefined`,
 * and silently falls through to a generic string, so the server's specific
 * refusal never reaches the user. On a billing surface that is unacceptable:
 * "you can't go below 15 seats" and "something went wrong" are not
 * interchangeable. This reads `description` first and tolerates `message` for
 * any endpoint that does return one.
 */
export function firstErrorMessage(
  errors: ReadonlyArray<{ description?: string; message?: string; code?: string }> | null | undefined,
): string | null {
  const first = errors && errors.length > 0 ? errors[0] : null;
  if (!first) return null;
  const text = first.description || first.message;
  return typeof text === "string" && text.trim().length > 0 ? text : null;
}

/* ─────────────────── Management denials (operator-managed) ─────────────────── */

/** THE codes. Contract with `BillingManagementErrorCodes.cs`; added to, never renamed. */
export const MANAGEMENT_DENIAL_CODES = [
  "SUBSCRIPTION_OPERATOR_MANAGED",
  "SUBSCRIPTION_BESPOKE_NO_PSP",
] as const;

/**
 * Pulls the management denial off ANY failure shape this client can see.
 *
 * ⚠️ IT MUST ACCEPT BOTH SHAPES, and that is not defensive padding. `/api/Billing/*` mutations
 * return HTTP 403 on a denial, and the axios interceptor in `utils/requestHandling.ts` REJECTS on
 * any non-2xx with the parsed body. So a denial arrives in the `catch` block, not as a returned
 * `Result` — while a `Result` with `errors[]` is still possible for callers that resolve. Reading
 * only one of the two is how a specific, translated, actionable refusal degrades into "something
 * went wrong", which is precisely the failure mode this feature exists to eliminate.
 *
 * Returns null for anything that is not a management denial, so callers fall through to their
 * existing error handling untouched.
 */
export function extractManagementDenial(source: unknown): BillingManagementDenialView | null {
  if (!source || typeof source !== "object") return null;
  const errors = (source as { errors?: unknown }).errors;
  if (!Array.isArray(errors)) return null;
  for (const raw of errors) {
    const err = raw as { code?: unknown; details?: unknown } | null | undefined;
    const code = typeof err?.code === "string" ? err.code : null;
    if (!code || !(MANAGEMENT_DENIAL_CODES as readonly string[]).includes(code)) continue;
    const details = (err?.details && typeof err.details === "object" ? err.details : null) as
      | Partial<BillingManagementDenialView>
      | null;
    // `details` is the contract, but a denial must still render specifically without it —
    // an unexplained 403 on a money surface is the outcome being designed out.
    return {
      code,
      action: details?.action ?? "StartCheckout",
      managementMode: details?.managementMode ?? "OperatorManaged",
      origin: details?.origin ?? "Unknown",
      state: details?.state ?? "Draft",
      messageKey:
        details?.messageKey ??
        (code === "SUBSCRIPTION_BESPOKE_NO_PSP"
          ? "billing.managed.bespokeNoPsp"
          : "billing.managed.operatorManaged"),
      contactEmail: details?.contactEmail ?? null,
      requestChangeEndpoint: details?.requestChangeEndpoint ?? null,
      operatorNote: details?.operatorNote ?? null,
    };
  }
  return null;
}

/**
 * Refusal code emitted by `BillingSubscriptionService` when the customer tries to end — or to let
 * lapse — a subscription the platform Operator has marked perpetual. Contract with
 * `BillingSubscriptionService.NeverExpiresCancelBlockedCode`; added to, never renamed.
 */
export const NEVER_EXPIRES_CANCEL_BLOCKED_CODE = "SUBSCRIPTION_NEVER_EXPIRES_CANCEL_BLOCKED";

/**
 * True when `source` carries `code` in its `errors[]`.
 *
 * ⚠️ IT MUST ACCEPT BOTH SHAPES, for exactly the reason spelled out on `extractManagementDenial`:
 * the server answers these refusals with HTTP 409, and the axios interceptor REJECTS on any non-2xx,
 * so the refusal arrives in the `catch` block rather than as a resolved `Result`. A caller that
 * inspected only the returned `Result` would degrade a specific, translated refusal into a generic
 * "something went wrong" — which is the whole failure mode this helper exists to prevent.
 */
export function hasBillingErrorCode(source: unknown, code: string): boolean {
  if (!source || typeof source !== "object") return false;
  const errors = (source as { errors?: unknown }).errors;
  if (!Array.isArray(errors)) return false;
  return errors.some((raw) => (raw as { code?: unknown } | null)?.code === code);
}

/**
 * Whether the CUSTOMER may mutate this subscription.
 *
 * FAILS OPEN when `management` is absent, on the same reasoning as `coerceAccessState`: the SERVER
 * is the guard, every mutating route re-checks, and a denial renders a specific explanation. So
 * failing open against an older server is cosmetic, while failing closed would show a paying
 * self-serve customer a false "we manage this for you" lockout over a subscription that is theirs.
 */
export function isCustomerManaged(
  management: BillingManagementNoticeView | null | undefined,
): boolean {
  if (!management) return true;
  return management.customerManaged !== false;
}

/* ───────────────────────── Endpoints ───────────────────────── */

/**
 * The list endpoint may return a bare array or the codebase's paged wrapper
 * depending on how the controller settles. Normalising here keeps that churn
 * out of the table component.
 */
function unwrapList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  const paged = payload as { items?: T[] } | null | undefined;
  return Array.isArray(paged?.items) ? (paged!.items as T[]) : [];
}

export async function getBillingOverview() {
  const response = await request.get<Result<BillingOverviewView>>("Billing/overview");
  return response.data;
}

export async function getSeatUsage() {
  const response = await request.get<Result<SeatUsageView>>("Billing/seats");
  return response.data;
}

export async function getSeatExplain() {
  const response = await request.get<Result<SeatExplainView>>("Billing/seats/explain");
  return response.data;
}

export async function previewSeatChange(input: SeatChangeInput) {
  const response = await request.post<Result<BillingQuoteView>>("Billing/seats/preview", input);
  return response.data;
}

export async function applySeatChange(input: SeatChangeInput) {
  const response = await request.post<Result<SeatChangeResultView>>("Billing/seats", input);
  return response.data;
}

export async function getInvoices() {
  const response = await request.get<Result<unknown>>("Billing/invoices");
  return {
    ...response.data,
    payload: unwrapList<BillingInvoiceRowView>(response.data?.payload),
  };
}

export interface BillingCreditNoteRowView {
  id: number;
  creditNoteNumber: string;
  billingInvoiceId: number;
  billingInvoiceNumber?: string | null;
  currency: string;
  totalGross: number;
  totalGrossFormatted: string;
  reasonKey: string;
  issuedAtUtc: string;
}

/**
 * BR-11 guarantees a credit note exists for every settled refund, and a VAT-registered finance team
 * legally needs it to reverse the input VAT they claimed on the original invoice. Until this call
 * existed there was no way to reach one from the portal at all — `downloadCreditNotePdf` had zero
 * callers and the refund email's "View your credit note" button led to a page without one.
 */
export async function getCreditNotes() {
  const response = await request.get<Result<unknown>>("Billing/credit-notes");
  return {
    ...response.data,
    payload: unwrapList<BillingCreditNoteRowView>(response.data?.payload),
  };
}

export async function getInvoice(id: number) {
  const response = await request.get<Result<BillingInvoiceRowView>>(`Billing/invoices/${id}`);
  return response.data;
}

/** Fetches the PDF as a blob and hands back an object URL + suggested filename. */
/**
 * The `locale` parameter is NOT optional chrome. `InvoiceDocumentRenderer` pins "Arial Unicode MS"
 * specifically so QuestPDF can render Arabic glyphs, and `BillingDocumentStrings` carries full Ar and
 * Fr tables plus an RTL flag — all of which was unreachable, because the client never sent a locale
 * and the controller defaults to `locale ?? "en"`. An Arabic-locale finance team downloaded an
 * English tax invoice.
 */
function documentLocale(locale?: string | null): string {
  const raw = (locale || i18n.language || "en").toLowerCase();
  const lang = raw.slice(0, 2);
  return lang === "ar" || lang === "fr" ? lang : "en";
}

export async function downloadInvoicePdf(
  id: number,
  invoiceNumber?: string | null,
  locale?: string | null,
) {
  const response = await request.get<Blob>(
    `Billing/invoices/${id}/pdf?locale=${documentLocale(locale)}`,
    { responseType: "blob" },
  );
  return saveBlob(response.data, `invoice-${invoiceNumber || id}.pdf`);
}

export async function downloadCreditNotePdf(
  id: number,
  creditNoteNumber?: string | null,
  locale?: string | null,
) {
  const response = await request.get<Blob>(
    `Billing/credit-notes/${id}/pdf?locale=${documentLocale(locale)}`,
    { responseType: "blob" },
  );
  return saveBlob(response.data, `credit-note-${creditNoteNumber || id}.pdf`);
}

function saveBlob(data: Blob, fileName: string): void {
  const blob = data instanceof Blob ? data : new Blob([data as unknown as BlobPart]);
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoke on the next tick so Safari has committed the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function getCreditBalance() {
  const response = await request.get<Result<BillingCreditView>>("Billing/credit");
  return response.data;
}

export async function getPaymentMethods() {
  const response = await request.get<Result<BillingPaymentMethodView[]>>("Billing/payment-methods");
  return response.data;
}

/**
 * Opens a hosted checkout / tokenising session purely to store a card.
 * No card data ever reaches our API: the browser talks to the PSP, we get a token.
 */
export async function startPaymentMethodSession(returnUrl: string, locale: string) {
  const response = await request.post<Result<BillingCheckoutStartedView>>(
    "Billing/payment-methods/session",
    { returnUrl, locale },
  );
  return response.data;
}

export async function removePaymentMethod(id: number) {
  const response = await request.delete<Result<boolean>>(`Billing/payment-methods/${id}`);
  return response.data;
}

export async function setDefaultPaymentMethod(id: number) {
  const response = await request.post<Result<boolean>>(
    `Billing/payment-methods/${id}/default`,
    {},
  );
  return response.data;
}

export async function startCheckout(input: StartCheckoutInput) {
  const response = await request.post<Result<BillingCheckoutStartedView>>("Billing/checkout", input);
  return response.data;
}

export async function getCheckoutStatus(sessionToken: string) {
  const response = await request.get<Result<BillingCheckoutStatusView>>(
    `Billing/checkout/${encodeURIComponent(sessionToken)}/status`,
  );
  return response.data;
}

export async function previewPlanChange(input: PlanChangeInput) {
  const response = await request.post<Result<BillingQuoteView>>("Billing/plan/preview", input);
  return response.data;
}

export async function applyPlanChange(input: PlanChangeInput) {
  const response = await request.post<Result<PlanChangeResultView>>("Billing/plan", input);
  return response.data;
}

export async function setAutoRenew(enabled: boolean) {
  const response = await request.post<Result<boolean>>("Billing/auto-renew", { enabled });
  return response.data;
}

export async function setAutoTrueUp(enabled: boolean) {
  const response = await request.post<Result<boolean>>("Billing/auto-true-up", { enabled });
  return response.data;
}

export async function previewCancellation(input: CancellationInput) {
  const response = await request.post<Result<CancellationQuoteView>>("Billing/cancel/preview", input);
  return response.data;
}

export async function cancelSubscription(input: CancellationInput) {
  const response = await request.post<Result<CancellationResultView>>("Billing/cancel", input);
  return response.data;
}

export async function getBillingProfile() {
  const response = await request.get<Result<BillingProfileView>>("Billing/profile");
  return response.data;
}

export async function saveBillingProfile(input: BillingProfileView) {
  const response = await request.put<Result<BillingProfileView>>("Billing/profile", input);
  return response.data;
}

/**
 * Ask the operator for a change to an operator-managed subscription.
 *
 * REUSE, NOT A NEW CHANNEL. `POST /api/Subscription/request-upgrade` already fans a notification
 * out to every Operator (deep-linked to the platform-billing tab for this company) AND emails the
 * configured operator mailbox with the `SubscriptionUpgradeRequest` template, whose EN/AR/FR files
 * all exist and ship today. A second channel would mean a fourth email template, and a missing
 * locale template is a SILENT DROP. There is deliberately no `companyId` on the wire: the tenant
 * comes from the authenticated principal, same as every `/api/Billing/*` route.
 *
 * The only addition is a topic prefix, client-side, so the operator's inbox says what was wanted.
 * It is written in English on purpose — it is read by the Mowazaf operator, not by the customer,
 * and the customer's own words follow it verbatim in their own language.
 */
export type BillingChangeTopic =
  | "Plan change"
  | "Seat change"
  | "Cancellation"
  | "Payment method"
  | "Renewal"
  | "General";

export async function requestBillingChange(topic: BillingChangeTopic, message: string) {
  const body = message.trim();
  return requestUpgrade({ message: `[${topic}] ${body}` });
}

/** Anonymous price list. Same source as mowazaf.com, so a price change is one edit. */
export async function getPublicPricing(currency: string, locale: string) {
  const params = new URLSearchParams({ currency, locale });
  const response = await request.get<Result<PublicPricingView>>(
    `BillingCatalog/plans?${params.toString()}`,
  );
  return response.data;
}
